import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';
import { VOCABULARIES } from 'volto-ministerio-publico-federal/config/vocabulary-config';
import { requireAltWhenOwnImage } from 'volto-ministerio-publico-federal/helpers/Image/altRequired';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_destaques_block',
    defaultMessage: 'Destaques (capa)',
  },
  list: { id: 'mpf_destaques_list', defaultMessage: 'Destaques' },
  item: { id: 'mpf_destaques_item', defaultMessage: 'Destaque' },
  fieldsetImage: {
    id: 'mpf_destaques_fieldset_image',
    defaultMessage: 'Imagem',
  },
  itemTitle: { id: 'mpf_destaques_item_title', defaultMessage: 'Título' },
  itemTitleHelp: {
    id: 'mpf_destaques_item_title_help',
    defaultMessage:
      'Até 60 caracteres é o que o Google costuma exibir sem cortar. Acima disso o título ainda aparece inteiro na capa.',
  },
  itemDescription: {
    id: 'mpf_destaques_item_description',
    defaultMessage: 'Linha de apoio',
  },
  itemDescriptionHelp: {
    id: 'mpf_destaques_item_description_help',
    defaultMessage: 'Exibida apenas na manchete.',
  },
  itemHref: { id: 'mpf_destaques_item_href', defaultMessage: 'Conteúdo' },
  itemHrefHelp: {
    id: 'mpf_destaques_item_href_help',
    defaultMessage:
      'Ao escolher uma notícia, título, linha de apoio e imagem são preenchidos. Campos já editados não são sobrescritos.',
  },
  itemImage: { id: 'mpf_destaques_item_image', defaultMessage: 'Imagem' },
  itemImageHelp: {
    id: 'mpf_destaques_item_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL. Em branco, usa a imagem da notícia.',
  },
  itemAlt: {
    id: 'mpf_destaques_item_alt',
    defaultMessage: 'Texto alternativo',
  },
  itemAltHelp: {
    id: 'mpf_destaques_item_alt_help',
    defaultMessage:
      'Descreva o conteúdo da imagem para leitores de tela. Obrigatório ao enviar imagem própria; herdando a imagem da notícia, herda-se também o texto alternativo dela.',
  },
  itemVideo: { id: 'mpf_destaques_item_video', defaultMessage: 'É vídeo' },
  itemVariant: { id: 'mpf_destaques_item_variant', defaultMessage: 'Posição' },
  itemVariantHelp: {
    id: 'mpf_destaques_item_variant_help',
    defaultMessage: 'Em branco, segue a ordem da lista.',
  },
  variantHero: {
    id: 'mpf_destaques_variant_hero',
    defaultMessage: 'Manchete (grande)',
  },
  variantTall: {
    id: 'mpf_destaques_variant_tall',
    defaultMessage: 'Card alto (ao lado da manchete)',
  },
  variantCard: {
    id: 'mpf_destaques_variant_card',
    defaultMessage: 'Card da segunda linha',
  },
  itemExternal: {
    id: 'mpf_destaques_item_external',
    defaultMessage: 'Abrir em nova aba',
  },
  temasFieldset: {
    id: 'mpf_destaques_temas_fieldset',
    defaultMessage: 'Navegue por temas',
  },
  temas: {
    id: 'mpf_destaques_temas',
    defaultMessage: 'Temas em destaque',
  },
  temasHelp: {
    id: 'mpf_destaques_temas_help',
    defaultMessage:
      'Selecione até 5 temas. Eles formam o bloco "Navegue por temas" que aparece na coluna lateral de TODAS as páginas de notícia (não só na capa) — cada tema leva o leitor à listagem geral filtrada por ele. Acima de 5, apenas os 5 primeiros são exibidos.',
  },
});

const destaqueItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'description', 'href', 'variant', 'external'],
    },
    {
      id: 'image',
      title: intl.formatMessage(messages.fieldsetImage),
      fields: ['image', 'alt', 'isVideo'],
    },
  ],
  properties: {
    // Contador em vez de corte no render: o título aparece inteiro na capa,
    // então o tamanho passa a ser decisão editorial, com aviso na hora de
    // escrever. Ver `widgets/CharCountWidget` sobre os 60 caracteres.
    title: {
      title: intl.formatMessage(messages.itemTitle),
      description: intl.formatMessage(messages.itemTitleHelp),
      widget: 'charCount',
      recommendedLength: SEO_TITLE_LENGTH,
    },
    description: {
      title: intl.formatMessage(messages.itemDescription),
      description: intl.formatMessage(messages.itemDescriptionHelp),
      widget: 'textarea',
    },
    href: {
      title: intl.formatMessage(messages.itemHref),
      description: intl.formatMessage(messages.itemHrefHelp),
      widget: 'object_browser',
      mode: 'link',
      // `image_field`/`hasPreviewImage` são colunas de catálogo reais e
      // `image_scales` é computado pelo restapi: juntos permitem herdar a
      // imagem da notícia sem o editor escolher arte dedicada.
      selectedItemAttrs: [
        '@id',
        '@type',
        'Title',
        'Description',
        'image_field',
        'hasPreviewImage',
        'image_scales',
        // Herda o texto alternativo da notícia quando a imagem é herdada.
        'descricaoImagem',
      ],
      allowExternals: true,
      initialPath: '/',
    },
    variant: {
      title: intl.formatMessage(messages.itemVariant),
      description: intl.formatMessage(messages.itemVariantHelp),
      choices: [
        ['hero', intl.formatMessage(messages.variantHero)],
        ['tall', intl.formatMessage(messages.variantTall)],
        ['card', intl.formatMessage(messages.variantCard)],
      ],
    },
    external: {
      title: intl.formatMessage(messages.itemExternal),
      type: 'boolean',
    },
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL — o
    // `object_browser` só escolhe conteúdo que já existe.
    image: {
      title: intl.formatMessage(messages.itemImage),
      description: intl.formatMessage(messages.itemImageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.itemAlt),
      description: intl.formatMessage(messages.itemAltHelp),
    },
    isVideo: {
      title: intl.formatMessage(messages.itemVideo),
      type: 'boolean',
    },
  },
  // `alt` entra em `required` só quando o editor envia imagem própria — o
  // `schemaExtender` de `items` cuida disso (ver `mpfDestaquesSchema`). Herdando
  // a imagem da notícia, herda-se também o `descricaoImagem` dela.
  required: ['title'],
});

/**
 * `activeObject`/`setActiveObject` são repassados pelo `Edit.tsx` e entram no
 * field spec de `items`: é o contrato que o `ObjectListWidget` do core espera
 * para aceitar um índice controlado de fora (ele só usa o par quando ambos
 * estão presentes). É o que permite ao lápis de um card abrir aquele item.
 */
export const mpfDestaquesSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: intl.formatMessage(messages.list),
      fields: ['items'],
    },
    {
      id: 'temas',
      title: intl.formatMessage(messages.temasFieldset),
      fields: ['temas'],
    },
  ],
  properties: {
    items: {
      title: intl.formatMessage(messages.list),
      widget: 'object_list',
      schema: destaqueItemSchema({ intl }),
      schemaExtender: requireAltWhenOwnImage,
      activeObject,
      setActiveObject,
    },
    // Curadoria dos temas da lateral "Navegue por temas". Fica aqui, junto dos
    // destaques, porque os dois são a mesma decisão editorial da capa que se
    // reflete em todas as notícias. `widget: 'array'` + `items.vocabulary`
    // força o ArrayWidget (multi-seleção); só `vocabulary` no topo cairia no
    // select de valor único. O ArrayWidget grava um array de tokens.
    temas: {
      title: intl.formatMessage(messages.temas),
      description: intl.formatMessage(messages.temasHelp),
      widget: 'array',
      items: { vocabulary: { '@id': VOCABULARIES.NEWS_THEMES.name } },
    },
  },
  required: [],
});

export default mpfDestaquesSchema;
